import React, { lazy, Suspense, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { perfDebug } from '@/lib/performance-tracker'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  ArrowLeft,
  Database,
  Table as TableIcon,
  AlertCircle,
} from 'lucide-react'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { SkeletonTable } from '@/components/ui/loading-states'
import { Alert, AlertDescription } from '@/components/ui/alert'
// Lazy load TableEditor to fix production circular dependency
// Chunk named "table-editor" for debugging, prefetch hint for performance
const TableEditor = lazy(
  () =>
    import(
      /* webpackChunkName: "table-editor" */
      /* webpackPrefetch: true */
      '@/components/table-editor/TableEditor'
    )
)
import {
  useProject,
  useScenario,
  useDataset,
  useOptimizationRuns,
} from '@/hooks/useVRPData'
import type { Id } from '../../../convex/_generated/dataModel'

/**
 * ChunkLoadErrorBoundary - Error boundary for lazy loading failures
 * Catches network errors, chunk 404s, and other dynamic import failures
 * Provides user-friendly "Reload Page" action to recover
 */
class ChunkLoadErrorBoundary extends React.Component<
  { children: React.ReactNode; navigate: (path: string) => void },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: {
    children: React.ReactNode
    navigate: (path: string) => void
  }) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error) {
    // ✅ FIX: Catch ALL errors, not just chunk load errors
    // This prevents infinite loop by properly handling ALL error types
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log all errors for monitoring
    if (import.meta.env.DEV) {
      console.error('[Error Boundary] Caught error:', error, errorInfo)
    }
  }

  // ✅ FIX: Add error recovery method (reset state without page reload)
  handleRetry = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      // Detect chunk load errors for specialized recovery UI
      const isChunkLoadError =
        this.state.error?.name === 'ChunkLoadError' ||
        this.state.error?.message?.includes('Failed to fetch') ||
        this.state.error?.message?.includes('Loading chunk') ||
        this.state.error?.message?.includes('dynamically imported module')

      return (
        <div className="flex flex-col h-full bg-background">
          <div className="flex items-center justify-center flex-1">
            <Card className="w-96">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-yellow-600">
                  <TableIcon className="w-5 h-5" />
                  {isChunkLoadError
                    ? 'Table Editor Failed to Load'
                    : 'Something Went Wrong'}
                </CardTitle>
                <CardDescription>
                  {isChunkLoadError
                    ? 'The table editor component could not be loaded. This might be due to a network issue or outdated cache.'
                    : 'An error occurred while processing your request. You can try again or go back to the projects page.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {import.meta.env.DEV && (
                  <Alert>
                    <AlertCircle className="w-4 h-4" />
                    <AlertDescription className="text-sm">
                      <strong>Development Mode:</strong>{' '}
                      {this.state.error?.message || 'Unknown error'}
                      {this.state.error?.stack && (
                        <pre className="mt-2 text-xs overflow-auto max-h-40">
                          {this.state.error.stack}
                        </pre>
                      )}
                    </AlertDescription>
                  </Alert>
                )}
                <div className="flex gap-2">
                  {isChunkLoadError ? (
                    <Button
                      onClick={() => window.location.reload()}
                      className="flex-1"
                    >
                      Reload Page
                    </Button>
                  ) : (
                    <Button onClick={this.handleRetry} className="flex-1">
                      Try Again
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => this.props.navigate('/projects')}
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )
    }

    // ✅ CRITICAL: Only render children when NO error
    return this.props.children
  }
}

const TableEditorPage = () => {
  const { projectId, scenarioId, datasetId, tableType } = useParams()
  const navigate = useNavigate()

  // Validate and type the URL parameters
  const validTableTypes = [
    'vehicles',
    'jobs',
    'locations',
    'routes',
    'vehicle-summary',
    'skills',
    'products',
  ] as const
  const isValidTableType = (
    type: string | undefined
  ): type is (typeof validTableTypes)[number] => {
    return type !== undefined && validTableTypes.includes(type as any)
  }

  // Fetch data for breadcrumb and validation
  const project = useProject(projectId as Id<'projects'>)
  const scenario = useScenario(scenarioId as Id<'scenarios'>)
  const dataset = useDataset(datasetId as Id<'datasets'>)

  // Performance tracking: Page mount
  useEffect(() => {
    if (tableType) {
      // Handle direct URL navigation (not clicked from card)
      if (!window.__VRP_PERF_STARTED) {
        perfDebug.startTracking(tableType, 'direct-navigation')
        window.__VRP_PERF_STARTED = true
      }
      perfDebug.markStep('perf:page-ready')
    }
  }, [tableType])

  // Performance tracking: Metadata start (when queries begin)
  useEffect(() => {
    if (tableType && !project) {
      perfDebug.markStep('perf:metadata-queries-start')
    }
  }, [tableType, project])

  // Performance tracking: Metadata ready
  useEffect(() => {
    if (tableType && project && scenario && dataset) {
      perfDebug.markStep('perf:metadata-queries-end')
    }
  }, [tableType, project, scenario, dataset])

  // Performance tracking: Mark when metadata is ready
  useEffect(() => {
    if (tableType && project && scenario && dataset) {
      // Mark when page metadata is ready (don't end tracking yet - table still loading)
      perfDebug.markStep('ui:metadata-ready')
      // NOTE: endTracking() will be called by TableEditor component when fully interactive
    }
  }, [tableType, project, scenario, dataset])

  // For vehicle-summary, get the latest optimization run
  const optimizationRuns = useOptimizationRuns(
    tableType === 'vehicle-summary'
      ? (scenarioId as Id<'scenarios'>)
      : undefined
  )
  const latestOptimizationRun =
    optimizationRuns && optimizationRuns.length > 0
      ? optimizationRuns[0] // Assuming runs are sorted by timestamp descending
      : undefined

  // Validate parameters
  if (!projectId || !scenarioId || !datasetId || !isValidTableType(tableType)) {
    return (
      <div className="flex flex-col h-full bg-background">
        <div className="flex items-center justify-center flex-1">
          <Card className="w-96">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <TableIcon className="w-5 h-5" />
                Invalid Parameters
              </CardTitle>
              <CardDescription>
                The table editor URL is missing required parameters.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Expected format:
                /projects/[projectId]/scenarios/[scenarioId]/datasets/[datasetId]/[tableType]
              </p>
              <Button onClick={() => navigate('/projects')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Projects
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  // Loading state
  if (
    project === undefined ||
    scenario === undefined ||
    dataset === undefined
  ) {
    return (
      <div className="flex flex-col h-full bg-background p-6">
        <div className="mb-6">
          <div className="h-8 w-48 bg-muted animate-pulse rounded mb-2" />
          <div className="h-4 w-96 bg-muted animate-pulse rounded" />
        </div>
        <SkeletonTable columns={6} rows={15} hasCheckbox hasActions />
      </div>
    )
  }

  // Error state - data not found
  if (!project || !scenario || !dataset) {
    return (
      <div className="flex flex-col h-full bg-background">
        <div className="flex items-center justify-center flex-1">
          <Card className="w-96">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <Database className="w-5 h-5" />
                Data Not Found
              </CardTitle>
              <CardDescription>
                The requested project, scenario, or dataset could not be found.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate('/projects')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Projects
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Chunk load error boundary for lazy loading failures */}
      <ChunkLoadErrorBoundary navigate={navigate}>
        {/* Suspense boundary with existing SkeletonTable fallback */}
        <Suspense
          fallback={
            <div className="flex flex-col h-full bg-background p-6">
              <div className="mb-6">
                <div className="h-8 w-48 bg-muted animate-pulse rounded mb-2" />
                <div className="h-4 w-96 bg-muted animate-pulse rounded" />
              </div>
              <SkeletonTable columns={6} rows={15} hasCheckbox hasActions />
            </div>
          }
        >
          {/* Table Editor - Fixed container with proper overflow context */}
          <div
            className="@container flex-1 overflow-hidden"
            style={{ minHeight: 0 }}
          >
            <TableEditor
              datasetId={datasetId as Id<'datasets'>}
              tableType={tableType}
              projectId={projectId as Id<'projects'>}
              scenarioId={scenarioId as Id<'scenarios'>}
              optimizationRunId={
                tableType === 'vehicle-summary'
                  ? latestOptimizationRun?._id
                  : undefined
              }
            />
          </div>
        </Suspense>
      </ChunkLoadErrorBoundary>
    </div>
  )
}

export default TableEditorPage
